import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { Subscription, forkJoin } from 'rxjs';
import { FeaturedFramesService, FeaturedFrameSearchParams } from '../../services/featured-frames.service';
import { AppStateService } from '../../services/app-state.service';
import { AuthService } from '../../services/auth.service';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';


interface FilterItem {
  id: any;
  name: string;
  slug?: string;
  activated: boolean;
}

@Component({
  selector: 'app-featured-frames',
  templateUrl: './featured-frames.component.html'
})
export class FeaturedFramesComponent implements OnInit, OnDestroy {
  isLoading = true;
  hasLoaded = false;
  products: any[] = [];
  totalRecords = 0;

  // Pagination
  currentPage = 1;
  pageSize = 24;

  // Search
  searchText = '';

  // Sort
  sortBy = 'popular_art';
  sortDir = 'desc';

  readonly sorts = [
   { id: 1, label: 'Popularity',        sortby: 'popular_art',   sortdir: 'desc' },
   { id: 2, label: 'Price low to high', sortby: 'net_price_tax', sortdir: 'asc'  },
   { id: 3, label: 'Price high to low', sortby: 'net_price_tax', sortdir: 'desc' },
   { id: 4, label: 'Newest',            sortby: 'created_at',    sortdir: 'desc' }
  ];
  selectedSort = this.sorts[0];

  // Filters
  productTypes: FilterItem[] = [
    { id: 'canvas',  name: 'Canvas', slug: 'canvas',  activated: false },
    { id: 'framing', name: 'Frame',  slug: 'framing', activated: false },
  ];
  typeOfFrames: FilterItem[] = [];
  frameStyles:  FilterItem[] = [];
  colors:       FilterItem[] = [];
  orientations: FilterItem[] = [];
  spaces:       FilterItem[] = [];
  sizes:        FilterItem[] = [];
  discounts:    FilterItem[] = [];
  priceMin = 1;
  priceMax = 9999;
  readonly priceMaxLimit = 9999;

  // Pending active IDs read from URL before filter arrays are loaded
  private pendingProductTypes:  string[] = [];
  private pendingFrameStyles:   string[] = [];
  private pendingTypeOfFrames:  string[] = [];
  private pendingColors:        string[] = [];
  private pendingOrientations:  string[] = [];
  private pendingDiscounts:     string[] = [];
  private filtersLoaded = false;

  // Facets from search response
  facets: any = {};

  // Filter panel & grid
  showFilters = false;
  gridCols = 3;
  showSortDropdown = false;
  expandedPanels: Record<string, boolean> = {
    material: false, typeOfFrame: false, frameStyle: false,
    color: false, orientation: false, pricing: false
  };

  private sub = new Subscription();
  // Featured add-to-cart now writes to the NEW backend's site cart.
  private siteApi = `${environment.newApiUrl}/api/site`;

  constructor(
    public router: Router,
    private route: ActivatedRoute,
    private featuredFramesService: FeaturedFramesService,
    private appState: AppStateService,
    private authService: AuthService,
    private http: HttpClient,
  ) {}

  ngOnInit() {
    // Filters and products load in PARALLEL — don't wait for filters before fetching products
    this.loadFilters(() => {
      this.filtersLoaded = true;
      this.applyPendingFilters();
      // Products are already loading/loaded from queryParamMap — no extra call needed
    });

    this.sub.add(this.route.queryParamMap.subscribe(qp => {
      // Read all URL params into component state
      this.searchText  = qp.get('searchtext') || '';
      this.currentPage = Number(qp.get('page')) || 1;

      const sortby  = qp.get('sortby')  || 'popular_art';
      const sortdir = qp.get('sortdir') || 'desc';
      const match = this.sorts.find(s => s.sortby === sortby && s.sortdir === sortdir);
      this.selectedSort = match || this.sorts[0];
      this.sortBy  = this.selectedSort.sortby;
      this.sortDir = this.selectedSort.sortdir;

      this.priceMin = Number(qp.get('pricelt')) || 1;
      this.priceMax = Number(qp.get('pricegt')) || this.priceMaxLimit;

      // Store pending filter IDs (used by applyPendingFilters when filter arrays are ready)
      this.pendingProductTypes = (qp.get('product_type') || '').split(',').filter(Boolean);
      this.pendingFrameStyles  = (qp.get('frame_style')  || '').split(',').filter(Boolean);
      this.pendingTypeOfFrames = (qp.get('type_of_frame')|| '').split(',').filter(Boolean);
      this.pendingColors       = (qp.get('color')        || '').split(',').filter(Boolean);
      this.pendingOrientations = (qp.get('orientation')  || '').split(',').filter(Boolean);
      this.pendingDiscounts    = (qp.get('discount')     || '').split(',').filter(Boolean);

      if (this.filtersLoaded) {
        this.applyPendingFilters();
      }

      // Always load products immediately from URL params — no need to wait for filter arrays
      this.loadProductsFromUrl(qp);
    }));

    window.scrollTo({ top: 0 });
  }

  ngOnDestroy() {
    this.sub.unsubscribe();
  }

  private applyPendingFilters() {
    this.productTypes.forEach(f  => f.activated = this.pendingProductTypes.includes(String(f.slug || f.id)));
    this.frameStyles.forEach(f   => f.activated = this.pendingFrameStyles.includes(String(f.id)));
    this.typeOfFrames.forEach(f  => f.activated = this.pendingTypeOfFrames.includes(String(f.id)));
    this.colors.forEach(f        => f.activated = this.pendingColors.includes(String(f.name || f.id)));
    this.orientations.forEach(f  => f.activated = this.pendingOrientations.includes(String(f.id)));
    this.discounts.forEach(f     => f.activated = this.pendingDiscounts.includes(String(f.id)));
  }

  private loadFilters(onDone?: () => void) {
    forkJoin([
      this.featuredFramesService.getOrientations(),
      this.featuredFramesService.getSizes(),
      this.featuredFramesService.getFrameStyles(),
      this.featuredFramesService.getColors(),
      this.featuredFramesService.getSpaces(),
      this.featuredFramesService.getDiscounts()
    ]).subscribe({
      next: (res: any[]) => {
        this.orientations = (res[0]?.data || []).map((o: any) => ({ id: o.id, name: o.name, activated: false }));
        this.sizes        = (res[1]?.data || []).map((s: any) => ({ id: s.id, name: s.name, activated: false }));
        this.frameStyles  = (res[2]?.data || []).map((f: any) => ({ id: f.id, name: f.name, activated: false }));
        this.colors       = (res[3]?.data || []).map((c: any) => ({ id: c.name, name: c.name, slug: c.name, activated: false }));
        this.spaces       = (res[4]?.data || []).map((s: any) => ({ id: s.id, name: s.name, activated: false }));
        this.discounts    = (res[5]?.data || []).map((d: any) => ({ id: d.id, name: d.name, activated: false }));
        if (onDone) onDone();
      },
      error: () => { if (onDone) onDone(); }
    });
  }

  // ── Push all current state into URL ─────────────────────────────────────
  private syncToUrl() {
    const params: any = {};

    if (this.currentPage > 1)                         params.page        = this.currentPage;
    if (this.sortBy  !== 'popular_art')               params.sortby      = this.sortBy;
    if (this.sortDir !== 'desc' || params.sortby)     params.sortdir     = this.sortDir;
    if (this.searchText)                              params.searchtext  = this.searchText;
    // Send both pricelt+pricegt together — API needs both for range query
    if (this.priceMin > 1 || this.priceMax < this.priceMaxLimit) {
      params.pricelt = this.priceMin;
      params.pricegt = this.priceMax;
    }

    const pt = this.productTypes.filter(f => f.activated).map(f => f.slug || f.id).join(',');
    const fs = this.frameStyles.filter(f => f.activated).map(f => f.id).join(',');
    const tf = this.typeOfFrames.filter(f => f.activated).map(f => f.id).join(',');
    const co = this.colors.filter(f => f.activated).map(f => f.name || f.id).join(',');
    const or = this.orientations.filter(f => f.activated).map(f => f.id).join(',');
    const di = this.discounts.filter(f => f.activated).map(f => f.id).join(',');

    if (pt) params.product_type  = pt;
    if (fs) params.frame_style   = fs;
    if (tf) params.type_of_frame = tf;
    if (co) params.color         = co;
    if (or) params.orientation   = or;
    if (di) params.discount      = di;

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: params,
      replaceUrl: false   // push new history entry → back button restores state
    });
  }

  // Called from queryParamMap — builds params directly from URL without waiting for filter arrays
  private loadProductsFromUrl(qp: any) {
    this.isLoading = true;
    const params: FeaturedFrameSearchParams = {
      page:    this.currentPage,
      images:  this.pageSize,
      sortby:  this.sortBy,
      sortdir: this.sortDir,
    };

    if (this.searchText)                  params.searchtext    = this.searchText;
    if (qp.get('product_type'))           params.product_type  = qp.get('product_type');
    if (qp.get('frame_style'))            params.frame_style   = qp.get('frame_style');
    if (qp.get('type_of_frame'))          params.type_of_frame = qp.get('type_of_frame');
    if (qp.get('color'))                  params.color         = qp.get('color');
    if (qp.get('orientation'))            params.orientation   = qp.get('orientation');
    if (qp.get('discount'))               params.discount      = qp.get('discount');
    // Both must be sent together for Solr range query: net_price_tax:[pricelt TO pricegt]
    const pricelt = Number(qp.get('pricelt')) || 0;
    const pricegt = Number(qp.get('pricegt')) || 0;
    if (pricelt > 0 || (pricegt > 0 && pricegt < this.priceMaxLimit)) {
      params.pricelt = pricelt || 1;
      params.pricegt = pricegt || this.priceMaxLimit;
    }

    this.featuredFramesService.searchFeaturedFrames(params).subscribe({
      next: (res: any) => {
        this.products     = res?.results || [];
        this.totalRecords = res?.total   || 0;
        this.facets       = res?.facet_field || {};
        this.isLoading    = false;
        this.hasLoaded    = true;
      },
      error: () => { this.isLoading = false; this.hasLoaded = true; }
    });
  }

  // Called after filter toggle/sort/page changes (filter arrays already populated)
  loadProducts() {
    this.syncToUrl(); // triggers queryParamMap → loadProductsFromUrl
  }

  // Facet count helpers
  getFacetCount(facetKey: string, itemKey: any): number {
    return this.facets[facetKey]?.[itemKey] || 0;
  }

  isFilterDisabled(facetKey: string, itemKey: any): boolean {
    return !this.facets[facetKey] || !this.facets[facetKey][itemKey];
  }

  // Sort
  onSortChange(sort: any) {
    this.selectedSort = sort;
    this.sortBy       = sort.sortby;
    this.sortDir      = sort.sortdir;
    this.currentPage  = 1;
    this.syncToUrl();
  }

  // Filter toggle
  toggleFilter(item: FilterItem) {
    item.activated   = !item.activated;
    this.currentPage = 1;
    this.syncToUrl();
  }

  // Active filter chips
  get activeFilters(): { label: string; type: string; item: FilterItem }[] {
    const chips: any[] = [];
    this.productTypes.filter(f => f.activated).forEach(f => chips.push({ label: f.name, type: 'product_type', item: f }));
    this.frameStyles.filter(f => f.activated).forEach(f  => chips.push({ label: f.name, type: 'frame_style',  item: f }));
    this.typeOfFrames.filter(f => f.activated).forEach(f => chips.push({ label: f.name, type: 'type_of_frame',item: f }));
    this.colors.filter(f => f.activated).forEach(f       => chips.push({ label: f.name, type: 'color',        item: f }));
    this.orientations.filter(f => f.activated).forEach(f => chips.push({ label: f.name, type: 'orientation',  item: f }));
    if (this.priceMin > 1 || this.priceMax < this.priceMaxLimit) {
      chips.push({ label: '₹' + this.priceMin + ' – ₹' + this.priceMax, type: 'price', item: null });
    }
    return chips;
  }

  removeFilter(chip: any) {
    if (chip.type === 'price') {
      this.priceMin = 1;
      this.priceMax = this.priceMaxLimit;
    } else {
      chip.item.activated = false;
    }
    this.currentPage = 1;
    this.syncToUrl();
  }

  clearAllFilters() {
    this.productTypes.forEach(f  => f.activated = false);
    this.frameStyles.forEach(f   => f.activated = false);
    this.typeOfFrames.forEach(f  => f.activated = false);
    this.colors.forEach(f        => f.activated = false);
    this.orientations.forEach(f  => f.activated = false);
    this.discounts.forEach(f     => f.activated = false);
    this.priceMin    = 1;
    this.priceMax    = this.priceMaxLimit;
    this.currentPage = 1;
    this.syncToUrl();
  }

  private priceDebounce: any;
  onPriceSliderChange() {
    if (this.priceMin > this.priceMax) {
      [this.priceMin, this.priceMax] = [this.priceMax, this.priceMin];
    }
    clearTimeout(this.priceDebounce);
    this.priceDebounce = setTimeout(() => {
      this.currentPage = 1;
      this.syncToUrl();
    }, 500);
  }

  applyFiltersAndClose() {
    this.showFilters = false;
    this.currentPage = 1;
    this.syncToUrl();
  }

  // Pagination
  get totalPages(): number { return Math.ceil(this.totalRecords / this.pageSize); }
  get pageNumbers(): number[] {
    const total = this.totalPages;
    if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
    const pages: number[] = [1];
    if (this.currentPage > 3) pages.push(-1);
    for (let i = Math.max(2, this.currentPage - 1); i <= Math.min(total - 1, this.currentPage + 1); i++) pages.push(i);
    if (this.currentPage < total - 2) pages.push(-1);
    pages.push(total);
    return pages;
  }

  goToPage(p: number) {
    if (p < 1 || p > this.totalPages || p === this.currentPage) return;
    this.currentPage = p;
    this.syncToUrl();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  navigateToDetail(product: any) {
    this.router.navigate(['/featured-frame', product.title_slug || product.id]);
  }

  private get sessionId(): string {
    let sid = localStorage.getItem('session_id');
    if (!sid) {
      sid = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
        const r = Math.random() * 16 | 0;
        return (c === 'x' ? r : (r & 0x3 | 0x8)).toString(16);
      });
      localStorage.setItem('session_id', sid);
    }
    return sid;
  }

  private get authHeaders() {
    const token = localStorage.getItem('auth_token');
    const h: any = { 'Content-Type': 'application/json' };
    if (token) h['Authorization'] = `Bearer ${token}`;
    return { headers: h };
  }

  // Add to cart from listing
  onAddToCart(event: Event, product: any) {
    event.stopPropagation();
    const user = this.authService.currentUser;

    if (user && (Number(user.customers_id) || Number(user.id)) > 0) {
      const customerId = Number(user.customers_id) || Number(user.id);
      this.http.post(`${this.siteApi}/cart/on-sale`, {
        on_sale_id: product.id,
        image_path: '',
        width: 1,
        height: 1,
        installation_pincode: 0,
        frame_id: 0
      }, this.authHeaders).subscribe({
        next: () => {
          this.appState.loadCartFromApi(true);
          this.router.navigate(['/checkout']);
        },
        error: () => this.appState.showToast('Failed to add to cart', 'info')
      });
    } else {
      this.http.post(`${this.siteApi}/cart/on-sale`, {
        on_sale_id: product.id,
        image_path: '',
        width: 1,
        height: 1,
        installation_pincode: 0,
        session_id: this.sessionId
      }).subscribe({
        next: () => {
          this.appState.showToast('Product added to cart successfully!', 'cart');
          this.appState.loadCartFromApi(true);
          this.router.navigate(['/checkout']);
        },
        error: () => this.appState.showToast('Failed to add to cart', 'info')
      });
    }
  }

  // Wishlist toggle
  onToggleWishlist(event: Event, product: any) {
    event.stopPropagation();
    const raw = product.net_price_tax || product.net_price
      || product.list_price_tax || product.list_price
      || product.price || 0;
    this.appState.toggleWishlist(String(product.id), {
      id: String(product.id),
      name: product.caption || product.title || product.name,
      image: product.list_thumbnail,
      price: Number(raw) || 0,
      wallpaper_type: 'Featured',
      slug: product.title_slug
    });
  }

  isInWishlist(product: any): boolean {
    return this.appState.wishlistIds.has(String(product.id));
  }
}
